import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { faWindowClose } from '@fortawesome/free-solid-svg-icons/faWindowClose';
import { faBriefcase } from '@fortawesome/free-solid-svg-icons/faBriefcase';
import { faCheckSquare } from '@fortawesome/free-solid-svg-icons/faCheckSquare';
import { faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons/faEnvelopeOpenText';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { faKey } from '@fortawesome/free-solid-svg-icons/faKey';
import { faList } from '@fortawesome/free-solid-svg-icons/faList';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faSitemap } from '@fortawesome/free-solid-svg-icons/faSitemap';
import { faStar } from '@fortawesome/free-solid-svg-icons/faStar';
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import { faPlayCircle } from '@fortawesome/free-solid-svg-icons/faPlayCircle';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons/faFilePdf';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, forkJoin, mergeMap, Observable, of, skip, Subscription, take } from 'rxjs';
import { InterventionInstanceReminderInterface } from '../../../models/interface/intervention-instance-reminder.interface';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { LessonInterface } from '../../../models/interface/lesson.interface';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { HelperService } from '../../../services/helper/helper.service';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { InterventionStore } from '../../../store/intervention/component-store/intervention.store';
import { QuestionnaireStore } from '../../../store/lesson-questionnaire/component-store/lesson-questionnaire.store';
import { ReminderActionTypes } from '../../../store/reminder/reminder.action';

@Component({
  selector: 'app-card-intervention-instance',
  templateUrl: './card-intervention-instance.component.html',
  styleUrls: ['./card-intervention-instance.component.scss'],
  providers: [InterventionInstanceStore, QuestionnaireStore, InterventionStore],
})
export class CardInterventionInstanceComponent implements OnInit, OnDestroy {
  @Output()
  public emitUpdatePerformed = new EventEmitter<boolean>();

  // Icons
  faSearch = faSearch;
  faList = faList;
  faUsers = faUsers;
  faKey = faKey;
  faEnvelopeOpenText = faEnvelopeOpenText;
  faSitemap = faSitemap;
  faBriefcase = faBriefcase;
  faCheckSquare = faCheckSquare;
  faStar = faStar;
  faPlus = faPlus;
  faHands = faHands;
  faHandsHelping = faHandsHelping;
  faWindowClose = faWindowClose;
  faPlayCircle = faPlayCircle;
  faEdit = faEdit;
  faInfoCircle = faInfoCircle;
  faFilePdf = faFilePdf;
  faEnvelope = faEnvelope;
  faTrash = faTrash;

  public instance: InterventionInstanceInterface;

  public users: UserInterface[] = [];
  public studyCollaborators: UserInterface[] = [];

  public initialInterventions: InterventionInterface[] = [];

  public allInterventionInstanceReminders$: Observable<InterventionInstanceReminderInterface[]>;

  public interventionInstanceReminders: InterventionInstanceReminderInterface[];

  public isManager$: Observable<boolean>;
  isECoachOfInstance = false;

  public profile$: Observable<ProfileInterface> = null;

  public profile: ProfileInterface;

  public questionnairesOfIntervention: {
    id: number;
    lessons: LessonInterface[];
    intervention: InterventionInterface;
  }[] = [];

  public param = { intervention_name: '...' };

  private questionnairesOfIntervention$: Observable<LessonInterface[]>
  private intervention$: Observable<InterventionInterface>;

  private groups$: Observable<StudyInterface[]>;

  private unpauseInstanceResponse$: Observable<any>;

  private isManagerSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private interventionInstanceStore: InterventionInstanceStore,
    private questionnaireStore: QuestionnaireStore,
    private interventionStore: InterventionStore,
    private store: Store<{
      myProfile: ProfileInterface;
      allInterventionInstanceReminders: InterventionInstanceReminderInterface[];
      collabGroups: StudyInterface[];
    }>,
  ) {
    this.profile$ = this.store.select('myProfile');
    this.allInterventionInstanceReminders$ = this.store.select('allInterventionInstanceReminders');
    this.groups$ = this.store.select('collabGroups');
    this.unpauseInstanceResponse$ = this.interventionInstanceStore.unpauseInstanceResponse$;
    this.intervention$ = this.interventionStore.intervention$;
    this.questionnairesOfIntervention$ = this.questionnaireStore.questionnairesOfIntervention$;
    this.isManager$ = this.isManagerSubject.asObservable();
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  @Input()
  set _interventionInstance(_interventionInstance: InterventionInstanceInterface) {
    if (_interventionInstance) {
      this.instance = _interventionInstance;
    }
  }

  @Input()
  set _users(_users: UserInterface[]) {
    if (_users) {
      this.users = _users;
    }
  }

  @Input()
  set _studyCollaborators(_studyCollaborators: UserInterface[]) {
    if (_studyCollaborators) {
      this.studyCollaborators = _studyCollaborators;
    }
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.profile$.subscribe((profile) => {
        this.profile = profile;
        this.isECoachOfInstance = this.helper.isECoachOfInstance(this.instance, profile.id);
      }),
    );

    this.subscriptions.push(
      this.allInterventionInstanceReminders$.subscribe((reminders) => {
        this.interventionInstanceReminders = reminders;
      }),
    );

    this.subscriptions.push(
      this.groups$.subscribe((studies: StudyInterface[]) => {
        const study = studies.find(
          (study) => study.id.toString() === this.instance.attributes.study_id.toString(),
        );
        if (study !== undefined) {
          const myRoleSlug = this.helper.getHighestRoleOfStudy(study);
          if (myRoleSlug === 'study.owner' || myRoleSlug === 'study.ecoachmanager') {
            this.isManagerSubject.next(true);
          }
        }
      }),
    );
  }

  getTruncatedUserName(patientId: number, studyId?: number): string {
    const name = this.helper.getCodeNameEmail(patientId, this.users, studyId);
    return name.length > 45 ? `${name.slice(0, 42)}...` : name;
  }

  public openDialogInterventionInstanceDetails(): void {
    this.helperDialog
      .openDialogInterventionInstanceDetails(this.instance.id, this.param)
      .afterClosed()
      .subscribe((_result: InterventionInstanceInterface) => {
        this.emitUpdatePerformed.emit(true);
      });
  }

  public openDialogInterventionInstanceUpdate(): void {
    const interventionId = this.instance.attributes.intervention_id;
    this.questionnaireStore.getQuestionnairesIntervention({ interventionId });
    const questionnaires = this.questionnairesOfIntervention$.pipe(skip(1), take(1));
    this.interventionStore.getInterventionAsEcoach(interventionId);
    const intervention = this.intervention$.pipe(skip(1), take(1));
    forkJoin({ questionnaires, intervention })
      .pipe(
        mergeMap((results) => {
          if (results.intervention) {
            this.param.intervention_name = results.intervention.attributes.name;
          }
          return this.helperDialog
            .openDialogInterventionInstanceUpdate(
              this.instance,
              this.param,
              this.helper.excludeUsersWithOnlyStudyAccess(this.studyCollaborators),
              results.intervention,
              this.users,
              results.questionnaires,
              this.profile.id,
              this.isManagerSubject,
            )
            .afterClosed()
            .pipe(
              mergeMap((value) => {
                if (value !== 'SUCCESS') {
                  if (this.isManagerSubject.value) {
                    this.interventionInstanceStore.unpauseInstanceECoachManager({ instanceId: this.instance.id });
                  } else {
                    this.interventionInstanceStore.unpauseInstance({ instanceId: this.instance.id });
                  }
                  return this.unpauseInstanceResponse$.pipe(
                    filter((resp) => !!resp),
                    take(1),
                    mergeMap(() => of(null)),
                  );
                } else {
                  return of(null);
                }
              }),
            );
        }),
      )
      .subscribe(() => { this.emitUpdatePerformed.emit(true); });
  }

  public openDialogInterventionInstanceCancel(): void {
    this.interventionStore.getInterventionAsEcoach(this.instance.attributes.intervention_id);
    this.intervention$
      .pipe(
        skip(1),
        take(1),
        mergeMap((intervention: InterventionInterface) => {
          if (intervention) {
            this.param.intervention_name = intervention.attributes.name;
          }
          return this.helperDialog
            .openDialogInterventionInstanceCancel(
              this.param,
              this.studyCollaborators,
              intervention,
              this.users,
              this.isManagerSubject.value,
              this.instance,
            )
            .afterClosed();
        })
      )
      .subscribe((result) => {
        if (result === 'SUCCESS') {
          this.emitUpdatePerformed.emit(true);
        }
      });
  }

  public openDialogInterventionInstanceDelete(): void {
    this.interventionStore.getInterventionAsEcoach(this.instance.attributes.intervention_id);
    this.intervention$
      .pipe(
        skip(1),
        take(1),
        mergeMap((intervention: InterventionInterface) => {
          if (intervention) {
            this.param.intervention_name = intervention.attributes.name;
          }
          return this.helperDialog
            .openDialogInterventionInstanceDelete(
              this.param,
              this.studyCollaborators,
              intervention,
              this.users,
              this.isManagerSubject.value,
              this.instance,
            )
            .afterClosed();
        })
      )
      .subscribe((result) => {
        if (result === 'SUCCESS') {
          this.emitUpdatePerformed.emit(true);
        }
      });
  }

  public openDialogInterventionInstanceReport(): void {
    this.interventionStore.getInterventionAsEcoach(this.instance.attributes.intervention_id);
    this.intervention$
      .pipe(
        skip(1),
        take(1),
        mergeMap((intervention: InterventionInterface) => {
          return this.helperDialog
            .openDialogInterventionInstanceReport(
              this.instance,
              intervention,
              this.studyCollaborators,
              this.users,
            )
            .afterClosed();
        }),
      )
      .subscribe(() => {});
  }

  public openDialogInterventionInstanceReminderCreation(): void {
    this.helperDialog
      .openDialogCreateInterventionInstanceReminder(this.instance.id, this.instance.attributes.patient_id)
      .afterClosed()
      .subscribe((_result) => {
        this.store.dispatch({
          type: ReminderActionTypes.getAllInterventionInstanceRemindersOfUserType,
          payload: {}
        });
      });
  }

  public openDialogInterventionInstanceReminderDetails(reminder: InterventionInstanceReminderInterface): void {
    this.helperDialog.openDialogInterventionInstanceReminderDetails(
      reminder,
      this.instance.attributes.study_id,
    ).afterClosed().subscribe(() => {
      this.store.dispatch({
        type: ReminderActionTypes.getAllInterventionInstanceRemindersOfUserType,
        payload: {},
      });
    });
  }

  public hasReminder(): InterventionInstanceReminderInterface | undefined {
    const foundReminder = this.interventionInstanceReminders.find(
      (reminder) => reminder.attributes.model_id.toString() === this.instance.id.toString()
    );
    return foundReminder;
  }

  stateAllowsReminders(state: string): boolean {
    return state !== 'completed' && state !== 'canceled';
  }

  public unpause(): void {
    if (this.isManagerSubject.value) {
      this.interventionInstanceStore.unpauseInstanceECoachManager({ instanceId: this.instance.id });
    } else {
      this.interventionInstanceStore.unpauseInstance({ instanceId: this.instance.id });
    }
    this.unpauseInstanceResponse$
      .pipe(
        filter(value => !!value),
        take(1),
      )
      .subscribe((_result) => {
        this.emitUpdatePerformed.emit(true);
      });
  }

  public getDetailedIntervention(interventionId: number): void {
    this.router.navigateByUrl(`interventions/${interventionId}/instances`).then(() => {});
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
